import React from "react";
import EventImage from "../../../../components/EventImage.jsx";
import { FaCalendarAlt, FaMapMarkerAlt, FaUser, FaEye, FaTicketAlt, FaFileInvoice } from "react-icons/fa";
import { fixS3ImageUrl } from "../../../../components/imageUtils";

const getBackgroundStyle = (rawBackground) => {
  if (!rawBackground) {
    return {};
  }

  let background = rawBackground;

  // Allow background to be either a real array, JSON-stringified array,
  // or a Postgres text[] string like {image,"https://..."}
  if (!Array.isArray(background) && typeof background === "string") {
    const trimmed = background.trim();

    // First, try JSON.parse (e.g. '["image","https://..."]')
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        background = parsed;
      }
    } catch {
      // If it's not JSON, handle Postgres array syntax
      if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
        const inner = trimmed.slice(1, -1);
        const parts = inner.split(",");
        if (parts.length > 0) {
          background = parts.map((p) => {
            const t = p.trim();
            if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
              return t.slice(1, -1);
            }
            return t;
          });
        }
      }
    }
  }

  if (!Array.isArray(background) || background.length === 0) {
    return {};
  }

  const mode = background[0];
  const value1 = background[1];
  const value2 = background[2];

  if (mode === "solid" && value1) {
    return {
      backgroundColor: value1,
      backgroundImage: "none",
    };
  }

  if (mode === "gradient" && value1 && value2) {
    return {
      backgroundImage: `linear-gradient(90deg, ${value1}, ${value2})`,
      backgroundColor: "transparent",
    };
  }

  if (mode === "image" && value1) {
    const fixedBgUrl = fixS3ImageUrl(value1);
    return {
      backgroundImage: `url(${fixedBgUrl})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundColor: "transparent",
    };
  }

  return {};
};

const TicketCard = ({
  ticket,
  formatDate,
  formatPrice,
  generatingTicket,
  generatingInvoice,
  onViewTicket,
  onDownloadTicket,
  onDownloadInvoice,
  selectionEnabled = false,
  isSelected = false,
  onSelectionChange = () => {},
}) => {
  const handleSelectionToggle = (event) => {
    if (selectionEnabled) {
      onSelectionChange(ticket.id, event.target.checked);
    }
  };

  return (
    <div className="ticket-card">
      <div className="ticket-card__header">
        <div className="ticket-card__header-left">
          {selectionEnabled && (
            <label className="ticket-card__checkbox" aria-label="Select ticket for combined invoice">
              <input
                type="checkbox"
                className="ticket-card__checkbox-input"
                checked={isSelected}
                onChange={handleSelectionToggle}
              />
            </label>
          )}
          <h3 className="ticket-card__title">{ticket.eventName}</h3>
        </div>
        <span className="ticket-card__status">{ticket.status || "Confirmed"}</span>
      </div>

      <div className="ticket-card__body">
        <div
          className="ticket-card__image-wrapper"
          style={getBackgroundStyle(ticket.background)}
        >
          <EventImage
            image={ticket.eventImage}
            coverimage={ticket.eventImage}
            alt={ticket.eventName}
            style={{ width: "100%", height: "180px", objectFit: "contain", borderRadius: 4, marginBottom: 15 }}
            loading="eager"
          />
        </div>

        <div className="ticket-card__info">
          <div className="ticket-card__info-row">
            <FaCalendarAlt />
            <span>{formatDate(ticket.eventDate)}</span>
          </div>

          {ticket.eventLocation && (
            <div className="ticket-card__info-row">
              <FaMapMarkerAlt />
              <span>{ticket.eventLocation}</span>
            </div>
          )}

          <div className="ticket-card__info-row">
            <FaUser />
            <span>
              {ticket.delegate?.name
                ? `${ticket.delegate.name} ${ticket.delegate.surname || ""}`.trim()
                : "Guest"}
            </span>
          </div>
        </div>

        <div className="ticket-card__meta-row">
          <div>
            <div className="ticket-card__meta-label">Ticket Type</div>
            <div className="ticket-card__meta-value">{ticket.ticketType}</div>
          </div>
          <div>
            <div className="ticket-card__meta-label">Price</div>
            <div className="ticket-card__price">{formatPrice(ticket.price)}</div>
          </div>
        </div>

        <div className="ticket-card__actions">
          <button
            type="button"
            className="ticket-card__button ticket-card__button--view"
            onClick={() => onViewTicket(ticket)}
          >
            <FaEye /> View
          </button>

          <button
            type="button"
            className={`ticket-card__button ticket-card__button--ticket${
              generatingTicket[ticket.id] ? " ticket-card__button--disabled" : ""
            }`}
            onClick={() => onDownloadTicket(ticket)}
            disabled={generatingTicket[ticket.id]}
          >
            <FaTicketAlt /> {generatingTicket[ticket.id] ? "Generating..." : "Ticket"}
          </button>

          <button
            type="button"
            className={`ticket-card__button ticket-card__button--invoice${
              generatingInvoice[ticket.id] ? " ticket-card__button--disabled" : ""
            }`}
            onClick={() => onDownloadInvoice(ticket)}
            disabled={generatingInvoice[ticket.id]}
          >
            <FaFileInvoice /> {generatingInvoice[ticket.id] ? "Generating..." : "Invoice"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketCard;
